import { CacheTag } from '~/store/services/CacheTag';

import api from './api';
import urlConstants from '~/lib/constants/url.constants';

export const VIEW_DETAIL_TYPE = {
  PAUSE: 'PAUSE',
  APPOINTMENT: 'APPOINTMENT',
  DISCHARGE: 'DISCHARGE',
} as const;

export const APPOINTMENT_STATUS = {
  UPCOMING: 'UPCOMING',
  FULFILLED: 'FULFILLED',
  NO_SHOW: 'NO_SHOW',
  CANCELLED_BY_PATIENT: 'CANCELLED_BY_PATIENT',
} as const;

export type AppointmentStatus =
  (typeof APPOINTMENT_STATUS)[keyof typeof APPOINTMENT_STATUS];

export const PARTICIPANT_TYPE = {
  PROVIDER: 'PROVIDER',
  PATIENT: 'PATIENT',
  SUPPORT: 'SUPPORT',
} as const;

export type ParticipantType =
  (typeof PARTICIPANT_TYPE)[keyof typeof PARTICIPANT_TYPE];

export interface PauseViewDetail {
  type: typeof VIEW_DETAIL_TYPE.PAUSE;
  reason: string;
  startDate: string;
}

export interface AppointmentParticipant {
  externalId: string;
  name: string;
  avatarUrl?: string | null;
  type: ParticipantType;
}

export interface AppointmentViewDetail {
  type: typeof VIEW_DETAIL_TYPE.APPOINTMENT;
  appointmentExternalId: string;
  noteExternalId?: string | null;
  noteStatus?: string | null;
  appointmentTime: string;
  durationMinutes: number;
  appointmentTypeLabel: string;
  /**
   * Canonical appointment type name (from the appointment_type reference table); used to type a
   * note created for the session. Null when the stored label has no reference match.
   */
  appointmentType?: string | null;
  zoomLink?: string | null;
  consentToRecord?: boolean | null;
  status: AppointmentStatus;
  participants: AppointmentParticipant[];
}

export interface DischargeViewDetail {
  type: typeof VIEW_DETAIL_TYPE.DISCHARGE;
  dischargeSessionDate: string;
  insuranceAuthEndDate?: string | null;
  dischargePlanStatus?: string | null;
  dischargePlanLink?: string | null;
}

export type PatientViewDetail =
  | PauseViewDetail
  | AppointmentViewDetail
  | DischargeViewDetail;

export interface PatientProviderDashboard {
  patientExternalId: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  pronouns: string[];
  admitDate: string;
  treatmentModality: string;
  patientStatus: string;
  diagnosis: string;
  colorLevel: string;
  programName: string;
  viewDetail?: PatientViewDetail | null;
}

export interface AttendedPatientsResponse {
  count: number;
}

export interface UnsignedNoteItem {
  noteExternalId: string;
  title: string;
  createdAt: string;
  patientExternalId: string;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientChosenName: string | null;
  patientPronouns: string[] | null;
  patientDateOfBirth: string | null;
}

const providerDashboardApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPatientsInView: build.query<
      PatientProviderDashboard[],
      { providerId: string; view: string; timezone?: string; date?: string }
    >({
      providesTags: [CacheTag.ProviderDashboard],
      query: ({ providerId, view, timezone, date }) => ({
        url: urlConstants.providerDashboard.getPatientsInView(
          providerId,
          view,
          timezone,
          date,
        ),
      }),
    }),
    getAttendedPatientsCount: build.query<
      AttendedPatientsResponse,
      { providerId: string }
    >({
      providesTags: [CacheTag.ProviderDashboard],
      query: ({ providerId }) => ({
        url: urlConstants.providerDashboard.getAttendedPatientsCount(
          providerId,
        ),
      }),
    }),
    getUnsignedNotes: build.query<UnsignedNoteItem[], { providerId: string }>({
      providesTags: [CacheTag.ProviderDashboard],
      query: ({ providerId }) => ({
        url: urlConstants.providerDashboard.getUnsignedNotes(providerId),
      }),
    }),
  }),
});

export const {
  useGetPatientsInViewQuery,
  useGetAttendedPatientsCountQuery,
  useGetUnsignedNotesQuery,
} = providerDashboardApi;
